import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Trash2 } from 'lucide-react';
import { listCatalogVersionAdoptionsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { Button, ConfirmDialog, QueryBoundary, useToast } from '../../../components/ui/index.js';
import { DetailPage } from '../../../components/page/DetailPage.js';
import { CatalogAdoptionStatusPanel } from '../../../components/catalog/CatalogAdoptionStatusPanel.js';
import { Can } from '../../../components/auth/Can.js';
import { useTabParam } from '../../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../../help/index.js';
import { type DaemonScope, daemonKeys, daemonReads, daemonWrites } from './daemon-queries.js';
import { DaemonScopeProvider, useDaemonScope } from './DaemonScopeContext.js';
import { DaemonStatusBar } from './DaemonStatusBar.js';
import { DaemonOverviewTab } from './DaemonOverviewTab.js';
import { DaemonConfigTab } from './DaemonConfigTab.js';
import { DaemonRunsTab } from './DaemonRunsTab.js';
import { DaemonFindingsTab } from './DaemonFindingsTab.js';
import { DaemonReflectionsTab } from './DaemonReflectionsTab.js';
import { DaemonTriggerList } from './DaemonTriggerList.js';
import { DaemonSupervisorPanel } from './DaemonSupervisorPanel.js';
import { DaemonHealthTab } from './DaemonHealthTab.js';
import { DaemonWorkflowTab } from './DaemonWorkflowTab.js';

const TAB_IDS = [
  'overview',
  'health',
  'workflow',
  'config',
  'triggers',
  'supervision',
  'runs',
  'findings',
  'reflections',
] as const;

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'health', label: 'Health' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'config', label: 'Configuration' },
  { id: 'triggers', label: 'Triggers' },
  { id: 'supervision', label: 'Supervision' },
  { id: 'runs', label: 'Runs' },
  { id: 'findings', label: 'Findings' },
  { id: 'reflections', label: 'Reflections' },
];

interface DaemonDetailPageProps {
  scope?: DaemonScope;
}

export const DaemonDetailPage: React.FC<DaemonDetailPageProps> = ({ scope = 'org' }) => (
  <DaemonScopeProvider scope={scope}>
    <DaemonDetailView />
  </DaemonScopeProvider>
);

const DaemonDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, title, basePath, entityLabel, policy } = useDaemonScope();
  const [tab, setTab] = useTabParam(TAB_IDS, 'overview');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const daemonId = Number(id);
  const reads = daemonReads(scope);
  const writes = daemonWrites(scope);
  const keys = daemonKeys(scope);

  const query = useQuery({ ...reads.get(daemonId), enabled: Number.isFinite(daemonId) });
  const daemon = query.data;
  const adoptionsQuery = useQuery({ ...listCatalogVersionAdoptionsOptions(), enabled: Number.isFinite(daemonId) });
  const adoptions = (adoptionsQuery.data?.data ?? []).filter(
    (adoption) => adoption.ownerType === 'daemon' && adoption.ownerId === daemonId,
  );

  const invalidateDaemon = () => queryClient.invalidateQueries({ queryKey: keys.get(daemonId) });
  const path = { id: String(daemonId) };

  const enable = useMutation({
    ...writes.enable(),
    onSuccess: async () => {
      notify(`${entityLabel} turned on`.replace(/^\w/, (c) => c.toUpperCase()));
      await invalidateDaemon();
    },
    onError: () => notify('Could not turn it on', 'danger'),
  });
  const disable = useMutation({
    ...writes.disable(),
    onSuccess: async () => {
      notify(`${entityLabel} turned off`.replace(/^\w/, (c) => c.toUpperCase()));
      await invalidateDaemon();
    },
    onError: () => notify('Could not turn it off', 'danger'),
  });
  const remove = useMutation({
    ...writes.remove(),
    onSuccess: async () => {
      notify(`${entityLabel} deleted`.replace(/^\w/, (c) => c.toUpperCase()));
      await queryClient.invalidateQueries({ queryKey: keys.list() });
      navigate(basePath);
    },
    onError: () => notify('Could not delete it', 'danger'),
  });
  const run = useMutation({
    ...writes.run(),
    onSuccess: async () => {
      notify('Run started');
      await Promise.all([invalidateDaemon(), queryClient.invalidateQueries({ queryKey: keys.runs(daemonId) })]);
    },
    onError: () => notify('Could not start a run', 'danger'),
  });
  const resetGovernance = useMutation({
    ...writes.resetGovernance(),
    onSuccess: async () => {
      notify('Failure circuit reset');
      await Promise.all([
        invalidateDaemon(),
        queryClient.invalidateQueries({ queryKey: keys.readiness(daemonId) }),
        queryClient.invalidateQueries({ queryKey: keys.runs(daemonId) }),
      ]);
    },
    onError: () => notify('Could not reset the failure circuit', 'danger'),
  });

  return (
    <DetailPage
      title={daemon?.name ?? `${entityLabel.replace(/^\w/, (c) => c.toUpperCase())}`}
      subtitle={daemon?.mandate ?? daemon?.description ?? undefined}
      breadcrumbs={[{ label: title, to: basePath }, { label: daemon?.name ?? 'Details' }]}
      helpContent={PAGE_HELP.daemons}
      badges={daemon && <DaemonStatusBar daemon={daemon} />}
      actions={
        daemon && (
          <Can policy={policy}>
            {daemon.enabled ? (
              <Button
                variant="secondary"
                size="sm"
                loading={disable.isPending}
                onClick={() => disable.mutate({ path })}
              >
                <Pause size={14} aria-hidden="true" />
                Turn off
              </Button>
            ) : (
              <Button variant="secondary" size="sm" loading={enable.isPending} onClick={() => enable.mutate({ path })}>
                <Play size={14} aria-hidden="true" />
                Turn on
              </Button>
            )}
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} aria-hidden="true" />
              Delete
            </Button>
          </Can>
        )
      }
      alert={
        <CatalogAdoptionStatusPanel
          adoptions={adoptions}
          loading={adoptionsQuery.isPending}
          title="Assigned library updates"
          onChanged={() => void invalidateDaemon()}
        />
      }
      tabs={daemon ? TABS : undefined}
      activeTab={tab}
      onTabChange={setTab}
    >
      <QueryBoundary query={query} label={`the ${entityLabel}`}>
        {(loaded) => (
          <>
            {tab === 'overview' && (
              <DaemonOverviewTab
                daemon={loaded}
                onRunNow={() => run.mutate({ path })}
                onResetGovernance={() => resetGovernance.mutate({ path })}
                isRunning={loaded.status === 'running' || run.isPending}
                isResettingGovernance={resetGovernance.isPending}
              />
            )}
            {tab === 'health' && <DaemonHealthTab daemon={loaded} />}
            {tab === 'workflow' && <DaemonWorkflowTab daemon={loaded} />}
            {tab === 'config' && <DaemonConfigTab daemon={loaded} />}
            {tab === 'triggers' && <DaemonTriggerList daemon={loaded} />}
            {tab === 'supervision' && <DaemonSupervisorPanel daemon={loaded} />}
            {tab === 'runs' && <DaemonRunsTab daemon={loaded} />}
            {tab === 'findings' && <DaemonFindingsTab daemon={loaded} />}
            {tab === 'reflections' && <DaemonReflectionsTab daemon={loaded} />}
            <ConfirmDialog
              open={confirmDelete}
              onClose={() => setConfirmDelete(false)}
              onConfirm={() => remove.mutate({ path })}
              title={`Delete ${entityLabel}`}
              message={`Delete "${loaded.name ?? entityLabel}"? Its runs and findings are removed with it.`}
              confirmLabel={`Delete ${entityLabel}`}
              loading={remove.isPending}
            />
          </>
        )}
      </QueryBoundary>
    </DetailPage>
  );
};
